import { ActionIcon, Text } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import { Button } from "../../../components/shared/Button/Button";
import { useTheme } from "../../../hooks";
import { useDispatch, useSelector } from "../../../redux/hooks";
import { clearNMEAAmendModalData } from "../../../redux/slices/generatorFeaturesSlice";
import { setSections } from "../../../redux/slices/generatorSlice";
import { insertValues } from "../../../redux/utils/insertValues";
import { Flex } from "../../../styled";
import { useTriggerAutoSave } from "../../hooks/useTriggerAutoSave";
import {
  applyNMEAAmendments,
  getOnlyNMEARows,
  INMEARow,
} from "../NMEAAutomationModal/NMEAAutomationModal.utils";
import NMEAAmendModalPreview from "./NMEAAmendModalPreview";
import { IRow } from "src/interfaces/Logbooks";

export type previewItem = {
  sectionIndex: number;
  rowIndex: number;
  logbookRow: IRow;
  nmeaRow: INMEARow;
  nmeaDataRow: any;
  sectionName?: string | null;
};

const NMEAAmendModal = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { setValue, getValues } = useFormContext();
  const triggerAutoSave = useTriggerAutoSave();

  const { NMEAAmendModalData } = useSelector(s => s.generatorFeaturesSlice);
  const { lbentry, sortOrder } = useSelector(s => s.generatorSlice);

  const isOpen = Boolean(NMEAAmendModalData?.candidates?.length);

  const previewItems: previewItem[] = useMemo(() => {
    if (!NMEAAmendModalData || !isOpen) return [];

    const { candidates } = NMEAAmendModalData;

    return candidates
      .map(candidate => {
        const { sectionIndex, rowIndex, nmeaDataRow } = candidate;
        const section = lbentry.sections[sectionIndex];
        if (!section) return null;

        const logbookRow = section.rows[rowIndex];
        if (!logbookRow) return null;

        const nmeaSections = getOnlyNMEARows(
          [section],
          [nmeaDataRow],
          0,
          undefined,
        );

        const nmeaRow = nmeaSections[0]?.rows[0] ?? null;
        if (!nmeaRow) return null;

        return {
          sectionIndex,
          rowIndex,
          logbookRow,
          nmeaRow,
          nmeaDataRow,
          sectionName: section.title ?? undefined,
        };
      })
      .filter(Boolean) as previewItem[];
  }, [NMEAAmendModalData, lbentry.sections, isOpen]);

  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!isOpen) {
      setSelectedRows({});
      return;
    }

    setSelectedRows(
      Object.fromEntries(
        previewItems.map(item => [
          `${item.sectionIndex}-${item.rowIndex}`,
          true,
        ]),
      ),
    );
  }, [isOpen, previewItems]);

  const handleDismiss = () => {
    dispatch(clearNMEAAmendModalData());
  };

  const handleToggleRow = (
    sectionIndex: number,
    rowIndex: number,
    checked: boolean,
  ) => {
    setSelectedRows(prev => ({
      ...prev,
      [`${sectionIndex}-${rowIndex}`]: checked,
    }));
  };

  const handleToggleSection = (sectionIndex: number, checked: boolean) => {
    setSelectedRows(
      Object.fromEntries(
        previewItems.map(item => [`${sectionIndex}-${item.rowIndex}`, checked]),
      ),
    );
  };

  const handleAccept = () => {
    if (!NMEAAmendModalData) return;

    const filledSections = insertValues(getValues(), lbentry.sections);

    const selectedCandidates = previewItems
      .filter(item => selectedRows[`${item.sectionIndex}-${item.rowIndex}`])
      .map(c => ({
        sectionIndex: c.sectionIndex,
        rowIndex: c.rowIndex,
        nmeaDataRow: { NMEAId: c.nmeaRow?.NMEAId, ...c.nmeaDataRow },
      }));

    const newSections = applyNMEAAmendments({
      originalSections: filledSections,
      candidates: selectedCandidates,
      setValue,
      sortOrder,
    });

    dispatch(setSections(newSections));
    dispatch(clearNMEAAmendModalData());
    triggerAutoSave();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 300,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: 50,
        background: "rgba(0,0,0,0.5)",
      }}>
      <div
        style={{
          background: theme.colors.background[0],
          border: `2px solid ${theme.colors.inputBorder[0]}`,
          borderRadius: 7,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          width: "70%",
          maxWidth: 960,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}>
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: "10px 18px",
            borderBottom: `1px solid ${theme.colors.inputBorder[0]}`,
          }}>
          <Text size="xl" c={theme.colors.text[0]}>
            NMEA Data Now Available
          </Text>
          <ActionIcon onClick={handleDismiss}>
            <IconX />
          </ActionIcon>
        </Flex>

        <div style={{ padding: "12px 18px", overflowY: "auto", flex: 1 }}>
          <Text fz={15} mb={4}>
            When this entry was created, some NMEA data had not yet arrived from
            the provider. That data is now available.
          </Text>
          <Text fz={15} mb={16}>
            The rows below were filled manually. Review the comparison and click{" "}
            <strong>Apply NMEA Data</strong> to update them automatically.
          </Text>

          <NMEAAmendModalPreview
            previewItems={previewItems}
            selectedRows={selectedRows}
            onToggleRow={handleToggleRow}
            onToggleSection={handleToggleSection}
          />
        </div>

        <Flex
          justify="flex-end"
          style={{
            padding: "12px 18px",
            borderTop: `1px solid ${theme.colors.inputBorder[0]}`,
          }}>
          <Button onClick={handleAccept}>Apply NMEA Data</Button>
        </Flex>
      </div>
    </div>
  );
};

export default NMEAAmendModal;
